import React, { useEffect } from "react";
import { Link, Redirect } from "react-router-dom";
import { Button } from "components/ui/button";
import {
  LandingHeader,
  HeroSection,
  ProductCard,
  IntegrateSection,
} from "components/landing";
import Terminal from "components/landing/terminal";
import Footer from "components/Footer";
import { useKas } from "contexts/KasContext";
import { DEFAULT_POST_LOGIN_REDIRECT } from "utils/redirects";
import { FullScreenLoadingSpinner } from "components/FullScreenLoader";

const LandingPage: React.FC = () => {
  useEffect(() => {
    document.title = "NVIDIA Brev";
  }, []);

  const { isUserLoggedIn, isLoading } = useKas();

  if (isLoading) {
    return <FullScreenLoadingSpinner />;
  }

  if (isUserLoggedIn) {
    return <Redirect to={DEFAULT_POST_LOGIN_REDIRECT} />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <LandingHeader />

      <section className="container mx-auto flex min-h-[calc(100vh-15vh)] flex-1 items-start xl:max-w-7xl">
        <div className="grid w-full grid-cols-1 items-start gap-8 px-10 pt-12 lg:grid-cols-2 lg:pt-24">
          <div className="space-y-6">
            <h1 className="text-3xl font-bold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              NVIDIA Brev
            </h1>
            <p className="text-muted-foreground max-w-xl text-lg text-pretty">
              The developer cloud to prototype and ship AI
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="lg" asChild>
                <Link to="/environment/new">Launch an Instance</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a
                  href="https://docs.nvidia.com/brev/getting-started/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Documentation
                </a>
              </Button>
            </div>
            <Terminal />
          </div>

          <div className="hidden lg:block">
            <HeroSection />
          </div>
        </div>
      </section>
      <hr className="h-[1px] w-full py-2 border-border" />

      <section
        id="products"
        className="relative mx-auto w-full px-4 py-24 lg:px-6 xl:max-w-7xl"
      >
        <div>
          <div className="mb-16">
            <h2 className="mb-4 text-3xl font-bold">Products</h2>
          </div>
          <div className="grid h-[400px] gap-8 md:grid-cols-1 lg:grid-cols-2">
            <ProductCard
              variant="gpu"
              title="GPU Instances"
              description="Launch GPUs across 20+ clouds from around the world. Access global compute from one terminal."
              href="/environment/new/public"
            />
            <ProductCard
              variant="launchables"
              title="Launchables"
              description="One-click deployable AI templates. Share and Launch pre-configured environments for GPU-accelerated software."
              href="/launchables"
            />
          </div>
        </div>
      </section>
      <hr className="h-[1px] w-full py-2 border-border" />

      <IntegrateSection />
      <hr className="h-[1px] w-full py-2 border-border" />

      <section
        id="programs"
        className="relative mx-auto w-full px-4 py-24 lg:px-6 xl:max-w-7xl"
      >
        <div>
          <div className="mb-16">
            <h2 className="mb-4 text-3xl font-bold">Programs on Brev</h2>
          </div>
          <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
            <div className="flex flex-col border-r border-l border-zinc-800 p-6">
              <h3 className="mb-3 text-xl font-bold">NVIDIA Inception</h3>
              <p className="text-muted-foreground text-sm">
                NVIDIA Inception nurtures startups revolutionizing industries
                with AI and data science. Get access to technical resources,
                go-to-market support, and GPU credits.
              </p>
              <div className="mt-6">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href="https://www.nvidia.com/en-us/startups/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn More
                  </a>
                </Button>
              </div>
            </div>

            <div className="flex flex-col border-r border-l border-zinc-800 p-6">
              <h3 className="mb-3 text-xl font-bold">
                NVIDIA Academic Grant Program
              </h3>
              <p className="text-muted-foreground text-sm">
                The NVIDIA Academic Grant Program advances academic research by
                providing world-class computing access and resources to
                researchers.
              </p>
              <div className="mt-6">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href="https://www.nvidia.com/en-us/industries/higher-education-research/academic-grant-program/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn More
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
